import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/Analytics.css";
import DateRangePicker from "./DateRangePicker";
import CustomDropdown from "./StatusDropdown";
import FilterIcon from "../icons/BusinessHub/FilterIcon";
import InfoIcon from "../icons/InfoIcon";
import CartIcon from "../icons/CartIcon";
import LeafeIcon from "../icons/LeafeIcon";
import ElectricIcon from "../icons/ElectricIcon";
import SpeakerIcon from "../icons/SpeakerIcon";
import VectorUpIcon from "../icons/VectorUpIcon";
import FacilityIcon from "../icons/FacilityIcon";
import DartIcon from "../icons/DartIcon";
import PointerIcon from "../icons/PointerIcon";
import DiscountIcon from "../icons/DiscountIcon";
import DollarIcon from "../icons/DollarIcon";

const chartWidth = 700;
const chartHeight = 220;
const chartBaseline = 205;

const parseDisplayDate = (value) => {
  if (!value || typeof value !== "string") return null;
  const [day, month, year] = value.split("/").map(Number);
  if (!day || !month || !year) return null;

  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const addDays = (date, amount) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + amount);
  return nextDate;
};

const formatAxisDate = (date) => `${date.getMonth() + 1}/${date.getDate()}`;

const formatTooltipDate = (date) =>
  date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const formatCurrency = (value) =>
  `$${Math.round(value).toLocaleString("en-US")}`;

const getChartDates = (fromValue, toValue) => {
  const fallbackFrom = new Date(2025, 4, 16);
  const parsedFrom = parseDisplayDate(fromValue) || fallbackFrom;
  const parsedTo = parseDisplayDate(toValue) || parsedFrom;
  const from = parsedFrom <= parsedTo ? parsedFrom : parsedTo;
  const to = parsedFrom <= parsedTo ? parsedTo : parsedFrom;
  const totalDays = Math.max(
    1,
    Math.round((to.getTime() - from.getTime()) / 86400000),
  );
  const labelCount = Math.min(10, totalDays + 1);

  return Array.from({ length: labelCount }, (_, index) => {
    const ratio = labelCount === 1 ? 0 : index / (labelCount - 1);
    return addDays(from, Math.round(totalDays * ratio));
  });
};

const peak = (position, center, width, height) =>
  height * Math.exp(-Math.pow((position - center) / width, 2));

const getSeriesValue = (index, length, series) => {
  const position = length === 1 ? 0 : index / (length - 1);

  if (series === "ad") {
    return Math.max(
      120,
      850 +
        800 * Math.sin(position * Math.PI * 4.8) +
        peak(position, 0.08, 0.08, 1700) +
        peak(position, 0.55, 0.035, 2700) +
        peak(position, 0.77, 0.08, 1200) +
        peak(position, 0.97, 0.035, 1500),
    );
  }

  if (series === "organic") {
    return Math.max(
      120,
      650 +
        700 * Math.sin(position * Math.PI * 4.2) +
        peak(position, 0.08, 0.08, 1500) +
        peak(position, 0.53, 0.045, 2400) +
        peak(position, 0.78, 0.075, 950) +
        peak(position, 0.97, 0.04, 1000),
    );
  }

  return Math.max(
    140,
    500 +
      480 * Math.sin(position * Math.PI * 5.1) +
      peak(position, 0.1, 0.09, 700) +
      peak(position, 0.35, 0.07, 900) +
      peak(position, 0.6, 0.08, 800) +
      peak(position, 0.88, 0.045, 2500),
  );
};

const getSmoothPath = (points) => {
  if (!points.length) return "";

  return points.reduce((path, point, index) => {
    if (index === 0) return `M${point.x.toFixed(2)} ${point.y.toFixed(2)}`;

    const previous = points[index - 1];
    const controlDistance = (point.x - previous.x) * 0.42;
    return `${path} C${(previous.x + controlDistance).toFixed(2)} ${previous.y.toFixed(2)} ${(point.x - controlDistance).toFixed(2)} ${point.y.toFixed(2)} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
  }, "");
};

const getChartSeries = (dates) => {
  const length = Math.max(2, dates.length);
  const rawSeries = {
    total: dates.map((_, index) => getSeriesValue(index, length, "total")),
    organic: dates.map((_, index) => getSeriesValue(index, length, "organic")),
    ad: dates.map((_, index) => getSeriesValue(index, length, "ad")),
  };
  const topValue = Math.max(4000, ...Object.values(rawSeries).flat()) * 1.08;

  const toPoints = (values) =>
    values.map((value, index) => ({
      x: dates.length === 1 ? 0 : (index / (dates.length - 1)) * chartWidth,
      y: chartBaseline - (value / topValue) * 194,
      value,
    }));

  return {
    topValue,
    total: toPoints(rawSeries.total),
    organic: toPoints(rawSeries.organic),
    ad: toPoints(rawSeries.ad),
  };
};

const getChartSvgUrl = (series) => {
  const buildSeries = (points, stroke, gradientId) => {
    const path = getSmoothPath(points);
    return `
      <path d="${path} L${chartWidth} ${chartHeight} L0 ${chartHeight} Z" fill="url(#${gradientId})"/>
      <path d="${path}" fill="none" stroke="${stroke}" strokeWidth="2" strokeLinecap="round"/>
    `;
  };

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${chartWidth} ${chartHeight}" preserveAspectRatio="none">
      <defs>
        <linearGradient id="redFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#f4668e" stop-opacity=".34"/>
          <stop offset="1" stop-color="#f4668e" stop-opacity=".05"/>
        </linearGradient>
        <linearGradient id="purpleFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#d758e9" stop-opacity=".34"/>
          <stop offset="1" stop-color="#d758e9" stop-opacity=".05"/>
        </linearGradient>
        <linearGradient id="greenFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#22c55e" stop-opacity=".36"/>
          <stop offset="1" stop-color="#22c55e" stop-opacity=".04"/>
        </linearGradient>
      </defs>
      ${buildSeries(series.ad, "#22c55e", "greenFill")}
      ${buildSeries(series.organic, "#d758e9", "purpleFill")}
      ${buildSeries(series.total, "#f4668e", "redFill")}
    </svg>
  `;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const getAdvertisingChartSvgUrl = (series) => {
  const buildLine = (points, stroke) => {
    const path = getSmoothPath(points);
    return `<path d="${path}" fill="none" stroke="${stroke}" strokeWidth="2.4" strokeLinecap="round"/>`;
  };

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${chartWidth} ${chartHeight}" preserveAspectRatio="none">
      ${buildLine(series.ad, "#475467")}
      ${buildLine(series.organic, "#10b981")}
    </svg>
  `;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const getAxisLabels = (topValue) =>
  [1, 0.75, 0.5, 0.25, 0].map((ratio) => {
    const value = Math.round((topValue * ratio) / 1000);
    return value ? `$${value}k` : "0";
  });

const analyticsFilterInitialState = {
  product: "",
  channel: "",
  from: "",
  to: "",
};

const analyticsProductOptions = [
  { label: "All Products", value: "" },
  { label: "Bamboo Face Cleansing Set", value: "bamboo-face-cleansing-set" },
  { label: "Lido Eco Tote Tool", value: "lido-eco-tote-tool" },
  { label: "Lavender Bath Pouf", value: "lavender-bath-pouf" },
  { label: "Lorem Ipsum", value: "lorem-ipsum-one" },
  { label: "Lorem Ipsum", value: "lorem-ipsum-two" },
];

const advertisingTrendProductOptions = [
  { label: "All Products", value: "" },
  { label: "Bamboo Face Cleansing Set", value: "bamboo-face-cleansing-set" },
  { label: "Jade Gua Cha Tool", value: "jade-gua-cha-tool" },
  { label: "Lavender Silk Pillowcase", value: "lavender-silk-pillowcase" },
  { label: "Lorem Ipsum", value: "lorem-ipsum-one" },
  { label: "Lorem Ipsum", value: "lorem-ipsum-two" },
];

const analyticsChannelOptions = [
  { label: "All Channels", value: "" },
  { label: "Organic", value: "organic" },
  { label: "Advertising", value: "advertising" },
];

const kpiTooltipContent = {
  "Total Sales": {
    title: "Total Sales",
    definition:
      "Total vendor sales revenue generated by valid orders during the selected period.",
  },
  "Total Platform Revenue": {
    title: "Total Platform Revenue",
    definition:
      "The total revenue generated by the platform during the selected time period, including all completed transactions.",
  },
  "Organic Sales %": {
    title: "Organic Sales %",
    definition:
      "The percentage of eligible sales generated without paid-advertising attribution.",
  },
  "Avg Selling Price": {
    title: "Avg Selling Price",
    definition:
      "The average net price paid per unit sold during the selected period.",
  },
  "Sales Velocity": {
    title: "Sales Velocity",
    definition:
      "The average number of eligible units sold per day during the selected period.",
  },
  "Ad Spend": {
    title: "Ad Spend",
    definition:
      "Total money spent on eligible vendor campaigns during the selected period.",
    formula: "Sum of eligible campaign spend.",
  },
  "Ad-attributed Sales": {
    title: "Ad-attributed Sales",
    definition:
      "Eligible vendor revenue attributed to users who interacted with an advertisement under the approved attribution rules.",
    formula: "Sum of attributed eligible vendor revenue.",
  },
  "Avg ACoS": {
    title: "Avg ACoS",
    definition: "Advertising spend as a percentage of ad-attributed sales.",
    formula: "Ad Spend / Ad-attributed Sales x 100",
  },
  ROAS: {
    title: "ROAS",
    definition:
      "Revenue generated for each monetary unit spent on advertising.",
    formula: "Ad-attributed Sales / Ad Spend",
  },
  CTR: {
    title: "CTR",
    definition:
      "The percentage of eligible ad impressions that generated a click.",
    formula: "Ad Clicks / Ad impressions x 100",
  },
  CVR: {
    title: "CVR",
    definition:
      "The percentage of eligible ad clicks that led to a completed attributed order.",
    formula: "Attributed Orders / Ad Clicks x100",
  },
};

function Analytics() {
  const filterPanelRef = useRef(null);
  const advertisingTrendFilterRef = useRef(null);
  const [activeKpiTooltip, setActiveKpiTooltip] = useState(null);
  const [activeKpiTab, setActiveKpiTab] = useState("sales");
  const [trendDateRange, setTrendDateRange] = useState({
    from: "16/05/2025",
    to: "02/06/2025",
  });
  const [activeTrendIndex, setActiveTrendIndex] = useState(null);
  const [isAnalyticsFilterOpen, setIsAnalyticsFilterOpen] = useState(false);
  const [analyticsFilterPlacement, setAnalyticsFilterPlacement] =
    useState("left");
  const [draftAnalyticsFilters, setDraftAnalyticsFilters] = useState(
    analyticsFilterInitialState,
  );
  const [appliedAnalyticsFilters, setAppliedAnalyticsFilters] = useState(
    analyticsFilterInitialState,
  );
  const [isAdvertisingTrendFilterOpen, setIsAdvertisingTrendFilterOpen] =
    useState(false);
  const [advertisingTrendFilterPlacement, setAdvertisingTrendFilterPlacement] =
    useState("left");
  const [draftAdvertisingTrendFilters, setDraftAdvertisingTrendFilters] =
    useState(analyticsFilterInitialState);
  const [appliedAdvertisingTrendFilters, setAppliedAdvertisingTrendFilters] =
    useState(analyticsFilterInitialState);

  const primaryKpis = [
    {
      title: "Total Sales",
      value: "$71,996",
      icon: <DollarIcon />,
      change: "-3.9%",
      direction: "down",
    },
    {
      title: "Total Platform Revenue",
      value: "$71,996",
      icon: <CartIcon />,
      change: "0.4%",
      direction: "down",
    },
    {
      title: "Organic Sales %",
      value: "65.07%",
      icon: <LeafeIcon />,
      change: "0.2%",
      direction: "neutral",
    },
    {
      title: "Avg Selling Price",
      value: "$36.86",
      icon: <DiscountIcon />,
      change: "0.5%",
      direction: "up",
    },
    {
      title: "Sales Velocity",
      value: "69.2",
      icon: <ElectricIcon />,
      change: "5.6%",
      direction: "down",
    },
  ];

  const secondaryKpis = [
    {
      title: "Total Sales",
      value: "$71,996.40",
      change: "3.9%",
      direction: "down",
    },
    {
      title: "Organic Sales %",
      value: "65.07%",
      change: "0.2%",
      direction: "neutral",
    },
    {
      title: "Avg Selling Price",
      value: "$36.86",
      change: "0.5%",
      direction: "up",
    },
    {
      title: "Sales Velocity",
      value: "69.2",
      change: "5.6%",
      direction: "down",
    },
  ];

  const advertisingPrimaryKpis = [
    {
      title: "Ad Spend",
      value: "$5,700",
      icon: <SpeakerIcon />,
      change: "2.1%",
      direction: "down",
    },
    {
      title: "Ad-attributed Sales",
      value: "$20,220",
      icon: <VectorUpIcon />,
      change: "4.3%",
      direction: "up",
    },
    {
      title: "Avg ACoS",
      value: "28.3%",
      icon: <FacilityIcon />,
      change: "1.2%",
      direction: "down",
    },
    {
      title: "CVR",
      value: "4.9%",
      icon: <DiscountIcon />,
      change: "0.4%",
      direction: "up",
    },
    {
      title: "ROAS",
      value: "3.5 x",
      icon: <DartIcon />,
      change: "2.3x",
      direction: "up",
    },
    {
      title: "CTR",
      value: "2.4%",
      icon: <PointerIcon />,
      change: "0.2%",
      direction: "up",
    },
  ];

  const advertisingSecondaryKpis = [
    {
      title: "Ad Spend",
      value: "$5,700",
      change: "2.1%",
      direction: "down",
    },
    {
      title: "Ad-attributed Sales",
      value: "$20,220",
      change: "4.3%",
      direction: "up",
    },
    {
      title: "Avg ACoS",
      value: "28.3%",
      change: "1.2%",
      direction: "down",
    },
    {
      title: "ROAS",
      value: "3.5x",
      change: "2.3x",
      direction: "up",
    },
    {
      title: "CTR",
      value: "2.4%",
      change: "0.2%",
      direction: "up",
    },
    {
      title: "CVR",
      value: "4.9%",
      change: "0.4%",
      direction: "up",
    },
  ];

  const productPerformance = [
    {
      product: "Wireless Earbuds Pro",
      sales: "$18,200",
      unitsSold: "620",
      avgSellingPrice: "$29.35",
      salesVelocity: "20.7/ day",
    },
    {
      product: "Smart Watch Series X",
      sales: "$14,800",
      unitsSold: "310",
      avgSellingPrice: "$47.74",
      salesVelocity: "10.3/ day",
    },
    {
      product: "Laptop Stand Deluxe",
      sales: "$9,400",
      unitsSold: "940",
      avgSellingPrice: "$10.00",
      salesVelocity: "31.3/ day",
    },
    {
      product: "USB-C Hub 7-in-1",
      sales: "$7,200",
      unitsSold: "1,200",
      avgSellingPrice: "$6.00",
      salesVelocity: "40.0/ day",
    },
    {
      product: "Mechanical keyboard",
      sales: "$6,100",
      unitsSold: "220",
      avgSellingPrice: "$27.73",
      salesVelocity: "7.3/ day",
    },
  ];

  const analyticsBodyNotes = [
    {
      id: "best-performing-sku",
      variant: "performance",
      title: "Best Performing SKU",
      productName: "Wireless Earbuds Pro",
      sku: "WEP-2024-BLK",
      sales: "$18,200",
      unitsSold: "620",
      comparison: {
        direction: "up",
        value: "24.3%",
        label: "vs prior",
      },
    },
    {
      id: "lowest-selling-product",
      variant: "selling",
      title: "Lowest Selling Product",
      productName: "Mechanical Keyboard",
      sku: "MKB-TKL-RGB",
      sales: "$6,100",
      unitsSold: "220",
      comparison: {
        direction: "down",
        value: "11.8%",
        label: "vs prior",
      },
    },
  ];

  const advertisingFunnelRows = [
    {
      label: "Impressions",
      value: "142,000",
      width: "100%",
      variant: "strong",
    },
    {
      label: "Clicks",
      value: "3,124",
      badge: "2.2% CTR",
      width: "72%",
      variant: "medium",
    },
    {
      label: "Orders",
      value: "156",
      badge: "5.0% CVR",
      width: "53%",
      variant: "light",
    },
    {
      label: "Revenue",
      value: "$8,320",
      width: "43%",
      variant: "light",
    },
  ];

  const renderAdvertisingFunnelRow = (item) => (
    <div className="analytics-advertising-funnel-row" key={item.label}>
      <div className="analytics-advertising-funnel-meta">
        <div>{item.label}</div>
        <div>{item.value}</div>
      </div>
      <div className="analytics-advertising-funnel-track">
        <div
          className={`analytics-advertising-funnel-fill analytics-advertising-funnel-fill-${item.variant}`}
          style={{ width: item.width }}
        ></div>
        {item.badge ? (
          <div className="analytics-advertising-funnel-badge">{item.badge}</div>
        ) : null}
      </div>
    </div>
  );

  const renderKpiIcon = (item) =>
    item.icon ? <div className="analytics-kpi-icon">{item.icon}</div> : null;

  const renderKpiChange = (item, showInfo = false) => (
    <div
      className={`analytics-kpi-change analytics-kpi-change-${item.direction}${showInfo ? " analytics-kpi-change-with-info" : ""}`}
    >
      <div className="analytics-kpi-change-icon">
        <div className="analytics-kpi-change-line"></div>
        <div className="analytics-kpi-change-head"></div>
      </div>
      <div className="analytics-kpi-change-text">{item.change}</div>
      {showInfo ? renderKpiInfoTooltip(item) : null}
    </div>
  );

  const openKpiTooltip = (item, target) => {
    const tooltip = kpiTooltipContent[item.title];
    if (!tooltip || !target) return;

    const rect = target.getBoundingClientRect();
    const popoverWidth = 205;
    const viewportPadding = 12;
    const left = Math.min(
      window.innerWidth - popoverWidth - viewportPadding,
      Math.max(viewportPadding, rect.right - popoverWidth),
    );

    setActiveKpiTooltip({
      ...tooltip,
      top: rect.bottom + 8,
      left,
    });
  };

  const closeKpiTooltip = () => {
    setActiveKpiTooltip(null);
  };

  const renderKpiInfoTooltip = (item) => {
    const tooltip = kpiTooltipContent[item.title];
    if (!tooltip) return null;

    return (
      <div
        className="analytics-kpi-info-wrap"
        onMouseEnter={(event) => openKpiTooltip(item, event.currentTarget)}
        onMouseLeave={closeKpiTooltip}
        onFocus={(event) => openKpiTooltip(item, event.currentTarget)}
        onBlur={closeKpiTooltip}
      >
        <button
          className="analytics-kpi-info-trigger"
          type="button"
          aria-label={`${tooltip.title} information`}
        >
          <InfoIcon />
        </button>
      </div>
    );
  };

  const renderPrimaryKpiCard = (item) => (
    <div
      className="analytics-primary-kpi-card"
      key={`primary-${item.title}-${item.value}`}
    >
      <div className="analytics-primary-kpi-top">
        <div className="analytics-primary-kpi-title">{item.title}</div>
        <div className="analytics-primary-kpi-actions">
          {renderKpiIcon(item)}
        </div>
      </div>
      <div className="analytics-primary-kpi-value">{item.value}</div>
      {renderKpiChange(item, true)}
    </div>
  );

  const renderSecondaryKpiCard = (item) => (
    <div
      className="analytics-secondary-kpi-card"
      key={`secondary-${item.title}-${item.value}`}
    >
      <div className="analytics-secondary-kpi-top">
        <div className="analytics-secondary-kpi-title">{item.title}</div>
        {renderKpiInfoTooltip(item)}
      </div>
      <div className="analytics-secondary-kpi-body">
        <div className="analytics-secondary-kpi-value">{item.value}</div>
        {renderKpiChange(item)}
      </div>
    </div>
  );

  const renderAnalyticsBodyNote = (item) => (
    <div
      className={`analytic-body-note-card analytic-body-note-grid-${item.variant}`}
      key={item.id}
    >
      <div className="analytic-body-note-content">
        <div className="analytic-body-note-title">{item.title}</div>
        <div className="analytic-body-note-product">{item.productName}</div>
        <div className="analytic-body-note-metrics">
          <div className="analytic-body-note-sku">{item.sku}</div>
          <div className="analytic-body-note-sales">{item.sales}</div>
          <div className="analytic-body-note-units">
            <span>{item.unitsSold}</span>
            <span>units</span>
          </div>
        </div>
      </div>
      <div
        className={`analytic-body-note-comparison analytic-body-note-comparison-${item.comparison.direction}`}
      >
        <div className="analytic-body-note-arrow" aria-hidden="true">
          <div className="analytic-body-note-arrow-line"></div>
          <div className="analytic-body-note-arrow-head"></div>
        </div>
        <div className="analytic-body-note-comparison-copy">
          <span>{item.comparison.value}</span>
          <span>{item.comparison.label}</span>
        </div>
      </div>
    </div>
  );

  const trendChart = useMemo(() => {
    const dates = getChartDates(trendDateRange.from, trendDateRange.to);
    const series = getChartSeries(dates);
    const tooltipIndex = Math.floor((dates.length - 1) / 2);

    return {
      axisLabels: getAxisLabels(series.topValue),
      chartBackground: getChartSvgUrl(series),
      labels: dates.map(formatAxisDate),
      dates,
      series,
      defaultIndex: tooltipIndex,
    };
  }, [trendDateRange]);
  const advertisingTrendChart = useMemo(
    () => ({
      axisLabels: ["32", "24", "16", "8", "0%"],
      chartBackground: getAdvertisingChartSvgUrl(trendChart.series),
      labels: trendChart.labels,
    }),
    [trendChart],
  );

  const activeIndex =
    activeTrendIndex === null ? trendChart.defaultIndex : activeTrendIndex;
  const activeDate = trendChart.dates[activeIndex] || trendChart.dates[0];
  const activePoints = {
    total: trendChart.series.total[activeIndex] || trendChart.series.total[0],
    organic:
      trendChart.series.organic[activeIndex] || trendChart.series.organic[0],
    ad: trendChart.series.ad[activeIndex] || trendChart.series.ad[0],
  };
  const activeLeft = `${(activePoints.total.x / chartWidth) * 100}%`;
  const activeTooltipTop = `${Math.max(
    28,
    Math.min(
      170,
      Math.min(
        activePoints.total.y,
        activePoints.organic.y,
        activePoints.ad.y,
      ) - 18,
    ),
  )}px`;
  const isTrendTooltipVisible = activeTrendIndex !== null;

  const getActiveDotStyle = (point) => ({
    left: `${(point.x / chartWidth) * 100}%`,
    top: `${(point.y / chartHeight) * 100}%`,
  });

  const handleTrendMouseMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (event.clientX - bounds.left) / bounds.width),
    );
    const nextIndex = Math.round(ratio * (trendChart.labels.length - 1));
    setActiveTrendIndex(nextIndex);
  };

  const getFilterPlacement = (target) => {
    const rect = target.getBoundingClientRect();
    const buttonCenter = rect.left + rect.width / 2;
    return buttonCenter < window.innerWidth / 2 ? "right" : "left";
  };

  useEffect(() => {
    if (!isAnalyticsFilterOpen) return undefined;

    const onOutsideClick = (event) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target)
      ) {
        setIsAnalyticsFilterOpen(false);
      }
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setIsAnalyticsFilterOpen(false);
    };

    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isAnalyticsFilterOpen]);

  useEffect(() => {
    if (!isAdvertisingTrendFilterOpen) return undefined;

    const onOutsideClick = (event) => {
      if (
        advertisingTrendFilterRef.current &&
        !advertisingTrendFilterRef.current.contains(event.target)
      ) {
        setIsAdvertisingTrendFilterOpen(false);
      }
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setIsAdvertisingTrendFilterOpen(false);
    };

    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isAdvertisingTrendFilterOpen]);

  const clearAnalyticsFilters = () => {
    setDraftAnalyticsFilters(analyticsFilterInitialState);
    setAppliedAnalyticsFilters(analyticsFilterInitialState);
    setIsAnalyticsFilterOpen(false);
  };

  const applyAnalyticsFilters = () => {
    setAppliedAnalyticsFilters(draftAnalyticsFilters);

    if (draftAnalyticsFilters.from && draftAnalyticsFilters.to) {
      setTrendDateRange({
        from: draftAnalyticsFilters.from,
        to: draftAnalyticsFilters.to,
      });
    }

    setIsAnalyticsFilterOpen(false);
  };

  const clearAdvertisingTrendFilters = () => {
    setDraftAdvertisingTrendFilters(analyticsFilterInitialState);
    setAppliedAdvertisingTrendFilters(analyticsFilterInitialState);
    setIsAdvertisingTrendFilterOpen(false);
  };

  const applyAdvertisingTrendFilters = () => {
    setAppliedAdvertisingTrendFilters(draftAdvertisingTrendFilters);

    if (draftAdvertisingTrendFilters.from && draftAdvertisingTrendFilters.to) {
      setTrendDateRange({
        from: draftAdvertisingTrendFilters.from,
        to: draftAdvertisingTrendFilters.to,
      });
    }

    setIsAdvertisingTrendFilterOpen(false);
  };

  const displayedPrimaryKpis =
    activeKpiTab === "advertising"
      ? advertisingPrimaryKpis
      : activeKpiTab === "sales"
        ? primaryKpis
        : [];
  const displayedSecondaryKpis =
    activeKpiTab === "advertising"
      ? advertisingSecondaryKpis
      : activeKpiTab === "sales"
        ? secondaryKpis
        : [];
  const primaryKpiGridMode =
    activeKpiTab === "advertising"
      ? "analytics-kpi-grid-primary-advertising"
      : "";
  const secondaryKpiGridMode =
    activeKpiTab === "advertising"
      ? "analytics-kpi-grid-secondary-advertising"
      : "";
  const trendTitle =
    activeKpiTab === "advertising" ? "Advertising Trend" : "Sales Trend";
  const shouldShowSalesDetails = activeKpiTab === "sales";
  const shouldShowAdvertisingDetails = activeKpiTab === "advertising";

  return (
    <div className="analytics-main-container">
      <div className="analytics-header">
        <div className="analytics-header-icon">
          <div className="analytics-icon-chart">
            <div className="analytics-icon-axis"></div>
            <div className="analytics-icon-bar analytics-icon-bar-one"></div>
            <div className="analytics-icon-bar analytics-icon-bar-two"></div>
            <div className="analytics-icon-bar analytics-icon-bar-three"></div>
          </div>
        </div>
        <div className="analytics-header-text">
          <div className="analytics-header-title">Analytics</div>
          <div className="analytics-header-description">
            Analytics helps businesses understand trends, measure performance,
            and turn data into actionable strategies for better outcomes.
          </div>
        </div>
      </div>
      <div className="analytics-controls-row">
        <div className="analytics-tabs">
          <div
            className={`analytics-tab ${activeKpiTab === "sales" ? "analytics-tab-selected" : ""}`}
            onClick={() => setActiveKpiTab("sales")}
          >
            Sales KPIs
          </div>
          <div
            className={`analytics-tab ${activeKpiTab === "advertising" ? "analytics-tab-selected" : ""}`}
            onClick={() => setActiveKpiTab("advertising")}
          >
            Advertising KPIs
          </div>
          <div
            className={`analytics-tab ${activeKpiTab === "customer" ? "analytics-tab-selected" : ""}`}
            onClick={() => setActiveKpiTab("customer")}
          >
            Customer Behavior
          </div>
        </div>
        <div
          className={`analytics-filter-wrap analytics-filter-wrap-open-${analyticsFilterPlacement}`}
          ref={filterPanelRef}
        >
          <div
            className={`analytics-filter-button ${isAnalyticsFilterOpen ? "analytics-filter-button-active" : ""}`}
            onClick={(event) => {
              setAnalyticsFilterPlacement(
                getFilterPlacement(event.currentTarget),
              );
              setDraftAnalyticsFilters(appliedAnalyticsFilters);
              setIsAnalyticsFilterOpen((prev) => !prev);
            }}
          >
            <div className="analytics-filter-icon">
              {/* <div className="analytics-filter-funnel"></div> */}
              <FilterIcon />
            </div>
            <div className="analytics-filter-text">Filter</div>
          </div>

          {isAnalyticsFilterOpen && (
            <div className="analytics-filter-panel">
              <div className="analytics-filter-panel-header">Filter</div>
              <div className="analytics-filter-panel-body">
                <div className="analytics-filter-panel-content">
                  <div className="analytics-filter-row">
                    <div className="analytics-filter-row-title">
                      <div className="analytics-filter-row-name">Products</div>
                      <div
                        className="analytics-filter-row-clear"
                        onClick={() =>
                          setDraftAnalyticsFilters((prev) => ({
                            ...prev,
                            product: "",
                          }))
                        }
                      >
                        Clear
                      </div>
                    </div>
                    <CustomDropdown
                      value={draftAnalyticsFilters.product}
                      onChange={(value) =>
                        setDraftAnalyticsFilters((prev) => ({
                          ...prev,
                          product: value,
                        }))
                      }
                      placeholder="All Products"
                      options={analyticsProductOptions}
                      rightIcon={
                        <div className="analytics-filter-dropdown-caret"></div>
                      }
                      className="analytics-filter-dropdown"
                      openClassName="analytics-filter-dropdown-open"
                      menuClassName="analytics-filter-dropdown-menu analytics-filter-products-menu"
                      optionClassName="analytics-filter-dropdown-option"
                    />
                  </div>

                  <div className="analytics-filter-row analytics-filter-date-row">
                    <div className="analytics-filter-row-title">
                      <div className="analytics-filter-row-name">
                        Date Range
                      </div>
                      <div
                        className="analytics-filter-row-clear"
                        onClick={() =>
                          setDraftAnalyticsFilters((prev) => ({
                            ...prev,
                            from: "",
                            to: "",
                          }))
                        }
                      >
                        Clear
                      </div>
                    </div>
                    <DateRangePicker
                      compact
                      fromValue={draftAnalyticsFilters.from}
                      toValue={draftAnalyticsFilters.to}
                      onRangeChange={(range) =>
                        setDraftAnalyticsFilters((prev) => ({
                          ...prev,
                          ...range,
                        }))
                      }
                      className="analytics-filter-date-range"
                      inputsClassName="analytics-filter-date-inputs"
                      inputColClassName="analytics-filter-date-col"
                      fieldClassName="analytics-filter-date-field"
                      popoverClassName="analytics-filter-date-popover"
                    />
                  </div>

                  <div className="analytics-filter-row">
                    <div className="analytics-filter-row-title">
                      <div className="analytics-filter-row-name">Channels</div>
                      <div
                        className="analytics-filter-row-clear"
                        onClick={() =>
                          setDraftAnalyticsFilters((prev) => ({
                            ...prev,
                            channel: "",
                          }))
                        }
                      >
                        Clear
                      </div>
                    </div>
                    <CustomDropdown
                      value={draftAnalyticsFilters.channel}
                      onChange={(value) =>
                        setDraftAnalyticsFilters((prev) => ({
                          ...prev,
                          channel: value,
                        }))
                      }
                      placeholder="All Channels"
                      options={analyticsChannelOptions}
                      rightIcon={
                        <div className="analytics-filter-dropdown-caret"></div>
                      }
                      className="analytics-filter-dropdown"
                      openClassName="analytics-filter-dropdown-open"
                      menuClassName="analytics-filter-dropdown-menu analytics-filter-channels-menu"
                      optionClassName="analytics-filter-dropdown-option"
                    />
                  </div>
                </div>

                <div className="analytics-filter-footer">
                  <div className="analytics-filter-footer-actions">
                    <div
                      className="analytics-filter-clear-all"
                      onClick={clearAnalyticsFilters}
                    >
                      Clear All
                    </div>
                    <div
                      className="analytics-filter-apply"
                      onClick={applyAnalyticsFilters}
                    >
                      Apply All
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="analytics-kpi-section">
        <div
          className={`analytics-kpi-grid analytics-kpi-grid-primary ${primaryKpiGridMode}`}
        >
          {displayedPrimaryKpis.map(renderPrimaryKpiCard)}
        </div>
        {shouldShowAdvertisingDetails && (
          <div className="analytics-advertising-detail-stack">
            <div className="analytics-trend-card analytics-advertising-trend-card">
              <div className="analytics-trend-header">
                <div className="analytics-chart-title">
                  Advertising Performance Trends
                </div>
                <div
                  className={`analytics-filter-wrap analytics-advertising-filter-wrap analytics-filter-wrap-open-${advertisingTrendFilterPlacement}`}
                  ref={advertisingTrendFilterRef}
                >
                  <button
                    className={`analytics-advertising-trend-filter ${isAdvertisingTrendFilterOpen ? "analytics-advertising-trend-filter-active" : ""}`}
                    type="button"
                    onClick={(event) => {
                      setAdvertisingTrendFilterPlacement(
                        getFilterPlacement(event.currentTarget),
                      );
                      setDraftAdvertisingTrendFilters(
                        appliedAdvertisingTrendFilters,
                      );
                      setIsAdvertisingTrendFilterOpen((prev) => !prev);
                    }}
                  >
                    <FilterIcon />
                    <span>Filter</span>
                  </button>

                  {isAdvertisingTrendFilterOpen && (
                    <div className="analytics-filter-panel analytics-advertising-filter-panel">
                      <div className="analytics-filter-panel-header">
                        Filter
                      </div>
                      <div className="analytics-filter-panel-body">
                        <div className="analytics-filter-panel-content">
                          <div className="analytics-filter-row">
                            <div className="analytics-filter-row-title">
                              <div className="analytics-filter-row-name">
                                Products
                              </div>
                              <div
                                className="analytics-filter-row-clear"
                                onClick={() =>
                                  setDraftAdvertisingTrendFilters((prev) => ({
                                    ...prev,
                                    product: "",
                                  }))
                                }
                              >
                                Clear
                              </div>
                            </div>
                            <CustomDropdown
                              value={draftAdvertisingTrendFilters.product}
                              onChange={(value) =>
                                setDraftAdvertisingTrendFilters((prev) => ({
                                  ...prev,
                                  product: value,
                                }))
                              }
                              placeholder="All Products"
                              options={advertisingTrendProductOptions}
                              rightIcon={
                                <div className="analytics-filter-dropdown-caret"></div>
                              }
                              className="analytics-filter-dropdown analytics-advertising-filter-dropdown"
                              openClassName="analytics-filter-dropdown-open"
                              menuClassName="analytics-filter-dropdown-menu analytics-advertising-filter-dropdown-menu"
                              optionClassName="analytics-filter-dropdown-option"
                            />
                          </div>

                          <div className="analytics-filter-row analytics-filter-date-row">
                            <div className="analytics-filter-row-title">
                              <div className="analytics-filter-row-name">
                                Date Range
                              </div>
                              <div
                                className="analytics-filter-row-clear"
                                onClick={() =>
                                  setDraftAdvertisingTrendFilters((prev) => ({
                                    ...prev,
                                    from: "",
                                    to: "",
                                  }))
                                }
                              >
                                Clear
                              </div>
                            </div>
                            <DateRangePicker
                              compact
                              fromValue={draftAdvertisingTrendFilters.from}
                              toValue={draftAdvertisingTrendFilters.to}
                              onRangeChange={(range) =>
                                setDraftAdvertisingTrendFilters((prev) => ({
                                  ...prev,
                                  ...range,
                                }))
                              }
                              className="analytics-filter-date-range analytics-advertising-filter-date-range"
                              inputsClassName="analytics-filter-date-inputs"
                              inputColClassName="analytics-filter-date-col"
                              fieldClassName="analytics-filter-date-field"
                              popoverClassName="analytics-filter-date-popover analytics-advertising-filter-date-popover"
                            />
                          </div>
                        </div>

                        <div className="analytics-filter-footer">
                          <div className="analytics-filter-footer-actions">
                            <div
                              className="analytics-filter-clear-all"
                              onClick={clearAdvertisingTrendFilters}
                            >
                              Clear All
                            </div>
                            <div
                              className="analytics-filter-apply"
                              onClick={applyAdvertisingTrendFilters}
                            >
                              Apply All
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="analytics-trend-body analytics-advertising-trend-body">
                <div className="analytics-trend-y-axis">
                  {advertisingTrendChart.axisLabels.map((item) => (
                    <div key={item}>{item}</div>
                  ))}
                </div>
                <div className="analytics-trend-plot">
                  <div
                    className="analytics-chart-render"
                    style={{
                      backgroundImage: advertisingTrendChart.chartBackground,
                    }}
                  ></div>
                  <div className="analytics-grid-line analytics-grid-line-one"></div>
                  <div className="analytics-grid-line analytics-grid-line-two"></div>
                  <div className="analytics-grid-line analytics-grid-line-three"></div>
                  <div className="analytics-grid-line analytics-grid-line-four"></div>
                  <div className="analytics-x-labels">
                    {advertisingTrendChart.labels.map((item) => (
                      <div className="analytics-x-label" key={item}>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="analytics-trend-legend">
                <div className="analytics-legend-item">
                  <div className="analytics-legend-dot analytics-legend-ad-attributed"></div>
                  <div>Ad Attributed Sales</div>
                </div>
                <div className="analytics-legend-item">
                  <div className="analytics-legend-dot analytics-legend-organic-sales"></div>
                  <div>Organic Sales</div>
                </div>
              </div>
            </div>
            <div className="analytics-advertising-funnel-card">
              <div className="analytics-chart-title">Advertising Funnel</div>
              <div className="analytics-advertising-funnel-list">
                {advertisingFunnelRows.map(renderAdvertisingFunnelRow)}
              </div>
            </div>
          </div>
        )}
        {/* <div
          className={`analytics-kpi-grid analytics-kpi-grid-secondary ${secondaryKpiGridMode}`}
        >
          {displayedSecondaryKpis.map(renderSecondaryKpiCard)}
        </div> */}
        {shouldShowSalesDetails && (
          <div className="analytic-body-note-grid">
            {analyticsBodyNotes.map(renderAnalyticsBodyNote)}
          </div>
        )}
      </div>
      {shouldShowSalesDetails && (
        <div className="analytics-chart-section">
          <div className="analytics-trend-card">
            <div className="analytics-trend-header">
              <div className="analytics-chart-title">{trendTitle}</div>
              <DateRangePicker
                compact
                fromValue={trendDateRange.from}
                toValue={trendDateRange.to}
                onRangeChange={setTrendDateRange}
                className="analytics-trend-date-range"
                inputsClassName="analytics-trend-date-inputs"
                inputColClassName="analytics-trend-date-col"
                fieldClassName="analytics-trend-date-field"
                popoverClassName="analytics-trend-date-popover"
              />
            </div>
            <div className="analytics-trend-body">
              <div className="analytics-trend-y-axis">
                {trendChart.axisLabels.map((item) => (
                  <div key={item}>{item}</div>
                ))}
              </div>
              <div
                className="analytics-trend-plot"
                onMouseMove={handleTrendMouseMove}
                onMouseLeave={() => setActiveTrendIndex(null)}
              >
                <div
                  className="analytics-chart-render"
                  style={{ backgroundImage: trendChart.chartBackground }}
                >
                  {isTrendTooltipVisible && (
                    <div
                      className="analytics-hover-guide"
                      style={{ left: activeLeft }}
                    ></div>
                  )}
                  {isTrendTooltipVisible && (
                    <div
                      className="analytics-active-dot analytics-active-dot-total"
                      style={getActiveDotStyle(activePoints.total)}
                    ></div>
                  )}
                  {isTrendTooltipVisible && (
                    <div
                      className="analytics-active-dot analytics-active-dot-organic"
                      style={getActiveDotStyle(activePoints.organic)}
                    ></div>
                  )}
                  {isTrendTooltipVisible && (
                    <div
                      className="analytics-active-dot analytics-active-dot-ad"
                      style={getActiveDotStyle(activePoints.ad)}
                    ></div>
                  )}
                </div>
                <div className="analytics-grid-line analytics-grid-line-one"></div>
                <div className="analytics-grid-line analytics-grid-line-two"></div>
                <div className="analytics-grid-line analytics-grid-line-three"></div>
                <div className="analytics-grid-line analytics-grid-line-four"></div>
                <div className="analytics-area analytics-area-total"></div>
                <div className="analytics-area analytics-area-organic"></div>
                <div className="analytics-area analytics-area-ad"></div>
                <div className="analytics-line analytics-line-total"></div>
                <div className="analytics-line analytics-line-organic"></div>
                <div className="analytics-line analytics-line-ad"></div>
                <div
                  className={`analytics-tooltip ${isTrendTooltipVisible ? "analytics-tooltip-visible" : ""}`}
                  style={{
                    left: activeLeft,
                    top: activeTooltipTop,
                  }}
                >
                  <div className="analytics-tooltip-date">
                    {formatTooltipDate(activeDate)}
                  </div>
                  <div className="analytics-tooltip-row">
                    <div className="analytics-tooltip-dot analytics-tooltip-dot-total"></div>
                    <div className="analytics-tooltip-label">Total Sales</div>
                    <div className="analytics-tooltip-value">
                      {formatCurrency(activePoints.total.value * 25)}
                    </div>
                  </div>
                  <div className="analytics-tooltip-row">
                    <div className="analytics-tooltip-dot analytics-tooltip-dot-organic"></div>
                    <div className="analytics-tooltip-label">Organic Sales</div>
                    <div className="analytics-tooltip-value">
                      {formatCurrency(activePoints.organic.value * 25)}
                    </div>
                  </div>
                  <div className="analytics-tooltip-row">
                    <div className="analytics-tooltip-dot analytics-tooltip-dot-ad"></div>
                    <div className="analytics-tooltip-label">
                      Ad Attributed Sales
                    </div>
                    <div className="analytics-tooltip-value">
                      {formatCurrency(activePoints.ad.value * 25)}
                    </div>
                  </div>
                </div>
                <div className="analytics-x-labels">
                  {trendChart.labels.map((item) => (
                    <div className="analytics-x-label" key={item}>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="analytics-trend-legend">
              <div className="analytics-legend-item">
                <div className="analytics-legend-dot analytics-legend-total"></div>
                <div>Total Sales</div>
              </div>
              <div className="analytics-legend-item">
                <div className="analytics-legend-dot analytics-legend-organic"></div>
                <div>Organic Sales</div>
              </div>
              <div className="analytics-legend-item">
                <div className="analytics-legend-dot analytics-legend-ad"></div>
                <div>Ad Attributed Sales</div>
              </div>
            </div>
          </div>
          <div className="analytics-donut-card">
            <div className="analytics-chart-title">Organic vs Ad Sales</div>
            <div className="analytics-donut-wrap">
              <div className="analytics-donut"></div>
            </div>
            <div className="analytics-donut-legend">
              <div className="analytics-legend-item">
                <div className="analytics-legend-dot analytics-legend-organic"></div>
                <div>Organic (65%)</div>
              </div>
              <div className="analytics-legend-item">
                <div className="analytics-legend-dot analytics-legend-ad"></div>
                <div>Ad Sales (35%)</div>
              </div>
            </div>
            <div className="analytics-donut-stats">
              <div className="analytics-donut-stat">
                <div className="analytics-donut-stat-label">Total Orders</div>
                <div className="analytics-donut-stat-value">1,953</div>
              </div>
              <div className="analytics-donut-stat">
                <div className="analytics-donut-stat-label">Units Sold</div>
                <div className="analytics-donut-stat-value">2,076</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {shouldShowSalesDetails && (
        <div className="analytics-product-performance-card">
          <div className="analytics-product-performance-title">
            Product Performance
          </div>
          <div className="analytics-performance-table">
            <div className="analytics-performance-row analytics-performance-head-row">
              <div className="analytics-performance-cell analytics-product-cell">
                Product
              </div>
              <div className="analytics-performance-cell">Sales</div>
              <div className="analytics-performance-cell">Unit Sold</div>
              <div className="analytics-performance-cell">
                Avg Selling Price
              </div>
              <div className="analytics-performance-cell">Sales Velocity</div>
            </div>
            {productPerformance.map((item) => (
              <div className="analytics-performance-row" key={item.product}>
                <div className="analytics-performance-cell analytics-product-cell analytics-product-name">
                  {item.product}
                </div>
                <div className="analytics-performance-cell analytics-product-sales">
                  {item.sales}
                </div>
                <div className="analytics-performance-cell">
                  {item.unitsSold}
                </div>
                <div className="analytics-performance-cell">
                  {item.avgSellingPrice}
                </div>
                <div className="analytics-performance-cell">
                  {item.salesVelocity}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeKpiTooltip
        ? createPortal(
            <div
              className="analytics-kpi-info-popover analytics-kpi-info-popover-portal"
              role="tooltip"
              style={{
                top: `${activeKpiTooltip.top}px`,
                left: `${activeKpiTooltip.left}px`,
              }}
            >
              <div className="analytics-kpi-info-header">
                <div className="analytics-kpi-info-title">
                  {activeKpiTooltip.title}
                </div>
              </div>
              <div className="analytics-kpi-info-label">Definition</div>
              <div className="analytics-kpi-info-copy">
                {activeKpiTooltip.definition}
              </div>
              {/* {activeKpiTooltip.formula ? (
                <>
                  <div className="analytics-kpi-info-label">Formula</div>
                  <div className="analytics-kpi-info-formula">
                    {activeKpiTooltip.formula}
                  </div>
                </>
              ) : null} */}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export default Analytics;
